import React, { useState, useRef, useCallback, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { usersApi } from 'api';
import { useGlobal } from 'contexts/globalContext';
import { Button } from 'components/_new/Button';
import { useAuth } from 'contexts/authContext';
import { Avatar } from 'components/_new/Avatar';
import { DialogAction, DialogBaseExtendProps } from 'components/_new/Dialog';
import { userDisplayName } from 'utils/userDisplayName';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/cropImage';

type ChangeAvatarForm = {
	avatar: File | null;
};

type ChangeAvatarDialogProps = DialogBaseExtendProps;

interface CropArea {
	x: number;
	y: number;
	width: number;
	height: number;
}

interface Point {
	x: number;
	y: number;
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];

export const ChangeAvatarDialog = ({ onHide, visible, ...props }: ChangeAvatarDialogProps) => {
	const { t } = useTranslation(['common']);
	const { toastRef } = useGlobal();
	const { currentUser, currentUserRefetch } = useAuth();

	const { mutate, isLoading } = useMutation(usersApi.editMyAvatar, {
		onSuccess: () => {
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('userPanel.myAccount.changeAvatarSuccessMessage'),
			});
			currentUserRefetch();
			onHide?.();
		},
	});

	const { handleSubmit, setValue } = useForm<ChangeAvatarForm>({
		defaultValues: { avatar: null },
	});

	const [originalFile, setOriginalFile] = useState<File | null>(null);
	const [incomingAvatar, setIncomingAvatar] = useState<string | null>(null);
	const [editMode, setEditMode] = useState(false);

	// React-easy-crop states
	const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [rotation, setRotation] = useState(0);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);

	const fileRef = useRef<HTMLInputElement>(null);

	// Funkcja do tworzenia canvas z przyciętego obszaru
	const handleSubmitForm = handleSubmit(async ({ avatar }) => {
		try {
			if (editMode && croppedAreaPixels && incomingAvatar) {
				const croppedBlob = await getCroppedImg(incomingAvatar, croppedAreaPixels, rotation);
				const editedFile = new File([croppedBlob], originalFile?.name || 'avatar.jpg', {
					type: 'image/jpeg',
				});

				mutate({ avatar: editedFile });
			} else if (avatar) {
				mutate({ avatar });
			}
		} catch (error) {
			toastRef?.current?.show({
				severity: 'error',
				detail: t('misc.imageProcessingError') || 'Błąd podczas przetwarzania obrazu',
			});
		}
	});

	const handleFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const {
				target: { files },
			} = event;
			if (files?.length) {
				const file = files[0];
				if (file) {
					const maxAllowedSize = 20 * 1024 * 1024;
					const isSizeAllowed = file.size <= maxAllowedSize;
					const isTypeAllowed = ALLOWED_FILE_TYPES.includes(file.type);
					if (!isTypeAllowed || !isSizeAllowed) {
						setValue('avatar', null);
						if (fileRef?.current) {
							fileRef.current.value = '';
						}
						if (!isTypeAllowed) {
							toastRef?.current?.show({ severity: 'warn', detail: t('misc.fileInvalidFileType') });
						}
						if (!isSizeAllowed) {
							toastRef?.current?.show({
								severity: 'warn',
								detail: t('misc.fileSizeExceeds', { count: 20 }),
							});
						}
						return;
					}

					setOriginalFile(file);
					setValue('avatar', file);
					if (fileRef?.current) {
						fileRef.current.value = '';
					}
					const reader = new FileReader();
					reader.onload = ((file) => (event) => {
						if (event?.target?.result) {
							setIncomingAvatar(String(event.target.result));
							setEditMode(false);
							setCrop({ x: 0, y: 0 });
							setZoom(1);
							setRotation(0);
							setCroppedAreaPixels(null);
						}
					})(file);
					reader.readAsDataURL(file);
				}
			}
		},
		[setValue, t, toastRef]
	);

	const handlePickFile = useCallback(() => {
		if (fileRef?.current) {
			fileRef.current.click();
		}
	}, []);

	const handleCancelIncomingAvatar = useCallback(() => {
		setIncomingAvatar(null);
		setOriginalFile(null);
		setEditMode(false);
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		setRotation(0);
		setCroppedAreaPixels(null);
		setValue('avatar', null);
	}, [setValue]);

	const handleEnterEditMode = useCallback(() => {
		setEditMode(true);
	}, []);

	const handleExitEditMode = useCallback(() => {
		setEditMode(false);
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		setRotation(0);
		setCroppedAreaPixels(null);
	}, []);

	const onCropComplete = useCallback((croppedArea: CropArea, croppedAreaPixels: CropArea) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}, []);

	const { mutate: deleteMyAvatarMutate, isLoading: deleteMyAvatarLoading } = useMutation(usersApi.deleteMyAvatar, {
		onSuccess: () => {
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('userPanel.myAccount.deleteAvatarSuccessMessage'),
			});
			currentUserRefetch();
			onHide?.();
		},
	});

	const handleDeleteCurrentAvatar = useCallback(() => {
		deleteMyAvatarMutate();
	}, [deleteMyAvatarMutate]);

	return (
		<DialogAction
			title={t('userPanel.myAccount.changeAvatar')}
			size={editMode ? 'lg' : 'sm'}
			actions={[
				{
					key: 'save',
					submit: true,
					form: 'changeAvatarForm',
					label: t('actions.save'),
					loading: isLoading,
					icon: 'check',
					iconSet: 'pi',
				},
			]}
			visible={visible}
			onHide={onHide}
			{...props}
		>
			<form
				id="changeAvatarForm"
				onSubmit={handleSubmitForm}
				className="flex flex-column align-items-center gap-3 min-w-0"
			>
				{!incomingAvatar && (
					<div className="flex flex-column align-items-center">
						<div className="avatar-current-container" onClick={handlePickFile} role="button" tabIndex={0}>
							<Avatar
								id={currentUser?.id}
								src={currentUser?.avatar}
								name={userDisplayName(currentUser!, null)}
								size="xl"
							/>
						</div>
						<div className="flex flex-row justify-content-center gap-2 w-full mt-2">
							<input
								ref={fileRef}
								type="file"
								accept={ALLOWED_FILE_TYPES.join(',')}
								onChange={handleFileChange}
								className="hidden-accessible"
								aria-describedby="file-info"
							/>
							<Button
								onClick={handlePickFile}
								label={t('userPanel.myAccount.pickFile')}
								variantSize="sm"
								loading={deleteMyAvatarLoading}
							/>
							{currentUser?.avatar && (
								<Button
									onClick={handleDeleteCurrentAvatar}
									label={t('actions.remove')}
									variant="danger"
									variantSize="sm"
									loading={deleteMyAvatarLoading}
								/>
							)}
						</div>
					</div>
				)}

				{incomingAvatar && !editMode && (
					<div className="flex flex-column align-items-center gap-2" id="file-info">
						<div className="relative">
							<img
								src={incomingAvatar}
								className="incoming-avatar-img border-round-circle"
								alt="Incoming avatar"
								style={{ width: '200px', height: '200px', objectFit: 'cover' }}
							/>
						</div>
						<div className="flex gap-2">
							<Button
								onClick={handleEnterEditMode}
								label={t('userPanel.myAccount.editImage') || 'Edytuj obraz'}
								variant="primary-outlined"
								variantSize="sm"
								icon="pencil"
								iconSet="pi"
							/>
							<Button
								onClick={handleCancelIncomingAvatar}
								label={t('actions.cancel') || 'Anuluj'}
								variant="danger"
								variantSize="sm"
							/>
						</div>
					</div>
				)}

				{incomingAvatar && editMode && (
					<div className="flex flex-column gap-4 w-full">
						<div className="flex flex-column md:flex-row gap-4 w-full min-w-0">
							{/* Panel kontroli */}
							<div
								className="flex flex-column gap-3 w-full md:w-auto"
								style={{ minWidth: 'min(250px, 100%)' }}
							>
								<h4 className="m-0">{t('userPanel.myAccount.imageEditor') || 'Edytor obrazu'}</h4>

								{/* Kontrolki zoom */}
								<div className="flex flex-column gap-2">
									<label className="text-sm font-semibold">
										{t('userPanel.myAccount.zoom') || 'Powiększenie'}
									</label>
									<input
										type="range"
										min="1"
										max="3"
										step="0.1"
										value={zoom}
										onChange={(e) => setZoom(Number(e.target.value))}
										className="w-full"
									/>
									<span className="text-xs text-color-secondary">{Math.round(zoom * 100)}%</span>
								</div>
								<div className="flex flex-column gap-2">
									<label className="text-sm font-semibold">
										{t('userPanel.myAccount.rotation') || 'Obrót'}
									</label>
									<div className="flex flex-wrap gap-1">
										<Button
											onClick={() => setRotation((prev) => prev - 90)}
											icon="undo"
											iconSet="pi"
											variant="primary-outlined"
											variantSize="sm"
											label={t('userPanel.myAccount.rotateLeft')}
											className="flex-1"
										/>
										<Button
											onClick={() => setRotation((prev) => prev + 90)}
											icon="redo"
											iconSet="pi"
											variant="primary-outlined"
											variantSize="sm"
											label={t('userPanel.myAccount.rotateRight')}
											className="flex-1"
										/>
									</div>
									<input
										type="range"
										min="-180"
										max="180"
										step="1"
										value={rotation}
										onChange={(e) => setRotation(Number(e.target.value))}
										className="w-full"
									/>
									<span className="text-xs text-color-secondary">{rotation}°</span>
								</div>
								<div className="flex flex-column gap-2 mt-2">
									<Button
										onClick={() => {
											setCrop({ x: 0, y: 0 });
											setZoom(1);
											setRotation(0);
										}}
										label={t('actions.reset') || 'Resetuj'}
										variant="primary-outlined"
										variantSize="sm"
										icon="refresh"
										iconSet="pi"
									/>
									<Button
										onClick={handleExitEditMode}
										label={t('actions.back') || 'Wstecz'}
										variant="primary-outlined"
										variantSize="sm"
									/>
								</div>
							</div>
							<div className="flex flex-column align-items-center gap-2 flex-1 w-full min-w-0">
								<h4 className="m-0">{t('userPanel.myAccount.cropArea') || 'Przytnij obraz'}</h4>

								<div
									style={{ position: 'relative', width: '100%', maxWidth: '400px', aspectRatio: '1' }}
								>
									<Cropper
										image={incomingAvatar}
										crop={crop}
										zoom={zoom}
										rotation={rotation}
										aspect={1}
										onCropChange={setCrop}
										onZoomChange={setZoom}
										onRotationChange={setRotation}
										onCropComplete={onCropComplete}
										cropShape="round"
										showGrid={false}
									/>
								</div>

								<p className="text-xs text-color-secondary text-center m-0">
									{t('userPanel.myAccount.cropNote') ||
										'Przeciągnij aby przesunąć, użyj kółka aby powiększyć'}
								</p>
							</div>
						</div>
					</div>
				)}
			</form>

			<style>{`
				.incoming-avatar-img {
					max-width: 200px;
					max-height: 200px;
					object-fit: cover;
				}

				.avatar-current-container {
					cursor: pointer;
					transition: opacity 0.2s;
				}

				.avatar-current-container:hover {
					opacity: 0.8;
				}

				input[type='range'] {
					-webkit-appearance: none;
					height: 4px;
					border-radius: 2px;
					background: #ddd;
					outline: none;
				}

				input[type='range']::-webkit-slider-thumb {
					-webkit-appearance: none;
					appearance: none;
					width: 16px;
					height: 16px;
					border-radius: 50%;
					background: var(--primary-color);
					cursor: pointer;
				}

				input[type='range']::-moz-range-thumb {
					width: 16px;
					height: 16px;
					border-radius: 50%;
					background: var(--primary-color);
					cursor: pointer;
					border: none;
				}
			`}</style>
		</DialogAction>
	);
};
